import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import GifPicker from './GifPicker';
import SecureFileUpload from './SecureFileUpload';
import { sanitizeInput } from '../utils/validation';
import '../styles/PassOnForm.css';

const PassOnForm = ({ onPassOn, isLoading, previousPassOn }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    text: '',
    imageUrl: '',
    imageFile: null
  });
  const [showGifPicker, setShowGifPicker] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value // Don't sanitize during typing, only on submit
    }));
  };

  const handleFileUpload = (uploadResult) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: uploadResult.url,
      imageFile: null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.text.trim()) {
      alert('Please fill in both name and message fields.');
      return;
    }

    const submissionData = {
      name: sanitizeInput(formData.name),
      text: sanitizeInput(formData.text),
      imageUrl: formData.imageUrl.trim() || null,
      imageFile: formData.imageFile
    };

    await onPassOn(submissionData);
    
    // Reset form
    setFormData({
      name: user?.displayName || '',
      text: '',
      imageUrl: '',
      imageFile: null
    });
  };

  const handleGifSelect = (gif) => {
    setFormData(prev => ({
      ...prev,
      imageUrl: gif.url,
      imageFile: null
    }));
    setShowGifPicker(false);
  };

  const openGifPicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowGifPicker(!showGifPicker);
  };

  return (
    <div className="pass-on-form">
      {previousPassOn && (
        <div className="previous-message-context">
          <h3>Replying to:</h3>
          <div className="previous-message">
            <p><strong>{previousPassOn.title || previousPassOn.name || 'Anonymous'}</strong> said:</p>
            <p>"{previousPassOn.description || previousPassOn.text}"</p>
          </div>
        </div>
      )}
      
      <form id="pass-on-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="name">Your Name *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Enter your name"
            required
            disabled={isLoading}
          />
        </div>

        <div className="form-group">
          <label htmlFor="text">Your Message *</label>
          <textarea
            id="text"
            name="text"
            value={formData.text}
            onChange={handleChange}
            placeholder="What's your reply?"
            rows="4"
            required
            disabled={isLoading}
          />
        </div>

        {formData.imageUrl && (
          <div className="form-group">
            <label>Selected Image/GIF Preview:</label>
            <div className="image-preview">
              <img 
                src={formData.imageUrl} 
                alt="Preview" 
                style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }}
              />
              <button 
                type="button" 
                className="remove-image-button"
                onClick={() => setFormData(prev => ({ ...prev, imageUrl: '', imageFile: null }))}
              >
                Remove
              </button>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Add Image/GIF (optional)</label>
          <div className="image-options">
            <input
              type="url"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              disabled={isLoading}
              className="image-url-input"
            />
            
            <SecureFileUpload
              onUploadComplete={handleFileUpload}
              userId={user?.uid || 'anonymous'}
              passOnId="temp"
              uploadType="passOn"
              className="form-file-upload"
            >
              <div className="upload-button">
                📁 Upload File
              </div>
            </SecureFileUpload>
            
            <button 
              type="button" 
              className="gif-picker-button"
              onClick={openGifPicker}
              disabled={isLoading}
            >
              {showGifPicker ? '🔽 Hide' : '🎬 GIFs'}
            </button>
          </div>
        </div>
      </form>

      <GifPicker 
        onGifSelect={handleGifSelect}
        isVisible={showGifPicker}
      />

      <button 
        type="submit" 
        form="pass-on-form"
        className="pass-on-button"
        disabled={isLoading}
      >
        {isLoading ? 'Posting Reply...' : 'Post Reply'}
      </button>
    </div>
  );
};

export default PassOnForm;